/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import React, { useRef } from 'react';
import styled from 'styled-components';
import {
    Icon,
    STACK_GAP,
    StackView,
    Text,
    TEXT_SIZE,
    SPACING_S,
    SPACING_M,
    SPACE_FILL,
    LinkButton,
    useTooltip,
} from '@veeam/components';
import { onMouseEnterHandlerDecorator, TooltipText } from '@veeam/components/lib/Grid/cellRenderers/helpers';

import infoIcon from '../../../../icons/statuses/info.svg';
import ignoreIcon from '../../../../icons/actions/ignore.svg';
import refreshIcon from '../../../../icons/actions/refresh.svg';

const TitleWithTooltipWrapper = styled.div`
    display: flex;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: ${SPACING_M};

    ${Text.selector} {
        margin-left: ${SPACING_S};
    }
`;

interface TitleElementProps {
    text: string;
}

const TitleWithTooltip = ({ text }: TitleElementProps) => {
    const ref = useRef<HTMLDivElement | null>(null);

    return useTooltip({
        renderContent: () => <TooltipText size={TEXT_SIZE.s}>{text}</TooltipText>,
        render: tooltipProps => (
            <TitleWithTooltipWrapper
                onMouseEnter={() => {
                    onMouseEnterHandlerDecorator(tooltipProps.onMouseEnter, ref)();
                }}
                onMouseLeave={() => {
                    tooltipProps.onMouseLeave();
                }}
                ref={ref}
            >
                <Icon src={infoIcon} />
                <Text truncate>{text}</Text>
            </TitleWithTooltipWrapper>
        ),
    });
};

const RefreshWrapper = styled(StackView)`
    overflow: hidden;
`;

interface NotificationRenderer {
    text?: string;
    refreshButtonText?: string;
    ignoreButtonText?: string;
    onRefresh?: () => void;
    onIgnore?: () => void;
}

export const refreshNotificationRenderer = ({
    text = 'The data set has been changed.',
    refreshButtonText = 'Refresh',
    ignoreButtonText = 'Ignore',
    onRefresh,
    onIgnore,
}: NotificationRenderer) => (
    <RefreshWrapper
        spaceFill={SPACE_FILL.all}
        gap={STACK_GAP.l}
        data-selector-id='grid-notification'
    >
        <TitleWithTooltip data-selector-id='refresh-notification-text' text={text} />

        { onRefresh && (
            <LinkButton
                data-selector-id='refresh-link'
                compact
                iconBefore={refreshIcon}
                onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    onRefresh();
                }}
            >
                {refreshButtonText}
            </LinkButton>
        )}

        { onIgnore && (
            <LinkButton
                data-selector-id='ignore-link'
                compact
                iconBefore={ignoreIcon}
                onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    onIgnore();
                }}
            >
                {ignoreButtonText}
            </LinkButton>
        )}
    </RefreshWrapper>
);